import { cn } from './cn'

/**
 * What a row *is* — drawn as a solid silhouette, straight from the approved
 * design draft.
 *
 * `folder` and `measure` are the draft's own paths, verbatim. `product`,
 * `formula` and `labour` continue the same language: one closed shape, no
 * interior lines, read at 18 px in a table cell where an outline icon turns
 * to mush.
 */
const SOLID = {
  folder:
    'M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  measure: 'M14.7 6.3a4 4 0 0 0 5 5l-8.4 8.4a2.1 2.1 0 0 1-3-3z',
  product: 'M12 3c3.6 4.1 6 7 6 9.6a6 6 0 0 1-12 0C6 10 8.4 7.1 12 3z',
  formula: 'M6 3.5h12v3.3h-7.2l4.6 5.2-4.6 5.2H18v3.3H6v-3.1l5.2-5.4L6 6.6z',
  // Work charged by the hour: a disc with the quarter past twelve taken out
  // of it. One closed shape, like the others, so it holds at 18 px.
  labour: 'M12 3a9 9 0 1 0 9 9h-9z',
  // Several things carried as one: three bars of a stack, the top one short.
  // A folder is a place entries are filed in and this is not one, so it may
  // not borrow that mark — a row wearing it in a tree that also draws folders
  // reads as a folder.
  bundle: 'M6 4h8v3.2H6zM4 9.4h16v3.2H4zM4 14.8h16V18H4z',
} as const

/**
 * What a button *does* — thin strokes, so an action never reads as heavy as
 * the thing it acts on. The draft's paths, at the draft's weights.
 */
const LINE = {
  close: { d: 'M18 6 6 18M6 6l12 12', width: 2.2 },
  delete: { d: 'M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14', width: 2 },
  caret: { d: 'm9 6 6 6-6 6', width: 2.4 },
} as const

export type GlyphName = keyof typeof SOLID | keyof typeof LINE

/**
 * The ink of a row's mark.
 *
 * The accent at three quarters: a column of them reads as texture, and the
 * mark of a row is a colour the app has a name for. Exported because an
 * operator can put a lucide icon in the place of one of these, and a mark that
 * changed hue when they did read as a different kind of thing.
 */
export const GLYPH_INK = 'text-primary opacity-75'

/**
 * One glyph of the app's own set.
 *
 * Not lucide: the catalogue an admin picks an icon *from* is lucide, and it is
 * drawn in outline. The chrome — the folder of a tree row, the mark of an
 * entry, the close of a pane — is this set, so the frame never competes with
 * the content inside it.
 */
export function Glyph({
  name,
  size = 18,
  className,
}: {
  name: GlyphName
  size?: number
  className?: string
}) {
  const solid = name in SOLID
  const shared = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'aria-hidden': true,
    // A caller's own ink still wins — it is passed last.
    className: cn('shrink-0', solid && GLYPH_INK, className),
  }

  if (solid) {
    return (
      <svg {...shared} fill="currentColor">
        <path d={SOLID[name as keyof typeof SOLID]} />
      </svg>
    )
  }

  const line = LINE[name as keyof typeof LINE]
  return (
    <svg
      {...shared}
      fill="none"
      stroke="currentColor"
      strokeWidth={line.width}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={line.d} />
    </svg>
  )
}
